import confetti from "canvas-confetti";

const GOLDS = ["#C9A24C", "#E4C877", "#B08A2E", "#F0E2BC", "#8C6D2A"];

// A tasteful, restrained gold burst — two soft side cannons + a light shimmer of particles.
export const celebrateMilestone = () => {
  const base = { colors: GOLDS, disableForReducedMotion: true, scalar: 0.9, ticks: 220 };
  confetti({ ...base, particleCount: 40, spread: 60, origin: { x: 0, y: 0.7 }, angle: 60, startVelocity: 42 });
  confetti({ ...base, particleCount: 40, spread: 60, origin: { x: 1, y: 0.7 }, angle: 120, startVelocity: 42 });
  setTimeout(() => {
    confetti({ ...base, particleCount: 30, spread: 90, origin: { x: 0.5, y: 0.4 }, startVelocity: 30, gravity: 0.8 });
  }, 180);
};

// Continuous gold fountain erupting from the hole once the goal is reached.
// Binds to a dedicated canvas so the burst originates exactly at the cup.
// Gentle streamers + confetti that rise just above the flag and fade before
// reaching the frame edges (short-lived, low-velocity, narrow spread).
// Returns a stop() cleanup function.
export const startHoleConfetti = (canvas, originX = 0.831, originY = 0.412) => {
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    return () => {};
  }
  const shoot = confetti.create(canvas, { resize: true, useWorker: false });
  const streamer = confetti.shapeFromPath({ path: "M0 0 L3 0 L3 16 L0 16 Z" });
  let running = true;
  const origin = { x: originX, y: originY };
  const pop = () => {
    if (!running) return;
    // Gold streamers only — thin ribbons rise just past the flag, then fade as they fall
    shoot({
      colors: GOLDS, origin, angle: 90, particleCount: 4, spread: 26,
      startVelocity: 16, gravity: 0.6, decay: 0.91, ticks: 90, scalar: 2.2,
      shapes: [streamer], disableForReducedMotion: true,
    });
    setTimeout(pop, 340);
  };
  pop();
  return () => {
    running = false;
    setTimeout(() => shoot.reset(), 500);
  };
};

// Milestone thresholds on the road to $1,000,000.
export const MILESTONES = [25000, 50000, 100000, 250000, 500000, 750000, 1000000];

// Returns the highest milestone crossed when going from prev -> current (or null).
export const crossedMilestone = (prev, current) => {
  let hit = null;
  for (const m of MILESTONES) {
    if (prev < m && current >= m) hit = m;
  }
  return hit;
};

const _lighten = (hex, amt) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, ((n >> 16) & 255) + amt);
  const g = Math.min(255, ((n >> 8) & 255) + amt);
  const b = Math.min(255, (n & 255) + amt);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// Continuous, contained celebration fountain bound to a canvas over the tracker.
// Gold + silver confetti (matching the champion modals) launches up from the
// grass line, arcs to near the top of the frame and falls back, staying inside
// the frame. A CSS edge-mask on the canvas fades particles before the edges.
// Continuous but restrained. Returns stop().
export const celebrateInBox = (canvas) => {
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return () => {};
  const shoot = confetti.create(canvas, { resize: true, useWorker: false });
  // gold + silver + cream, same celebratory family as the champion rank modal
  const colors = ["#C9A24C", "#E4C877", "#B08A2E", "#D8DCE2", "#AAB1BA", "#FBF7EF"];
  // Slow, floaty upward fountain: low gravity + short life so particles rise to
  // just below the top of the frame and fade out before falling to the bottom.
  const base = {
    colors, disableForReducedMotion: true, scalar: 0.9,
    gravity: 0.9, decay: 0.94, ticks: 300,
  };
  let running = true;
  const launch = () => {
    if (!running) return;
    // gentle shower drifting down from the top of the frame across the full width
    shoot({ ...base, particleCount: 5, spread: 70, angle: 270, startVelocity: 18, origin: { x: Math.random(), y: -0.05 } });
    setTimeout(launch, 220);
  };
  launch();
  return () => { running = false; setTimeout(() => shoot.reset(), 800); };
};

// Big, dramatic, lingering single-hue celebration across the whole browser for a
// top-5 rank reveal. Runs for ~6 seconds total including a sustained shimmer fall.
export const celebrateRank = (hex) => {
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  const colors = [hex, _lighten(hex, 55), _lighten(hex, 110), "#FBF7EF"];
  const base = { colors, disableForReducedMotion: true, ticks: 400, zIndex: 200 };

  // 1) Immediate huge central pop
  confetti({ ...base, particleCount: 260, spread: 165, origin: { x: 0.5, y: 0.42 }, startVelocity: 62, scalar: 1.15 });
  // 2) Two powerful corner cannons
  confetti({ ...base, particleCount: 200, spread: 90, angle: 60, origin: { x: 0, y: 0.75 }, startVelocity: 70, scalar: 1.1 });
  confetti({ ...base, particleCount: 200, spread: 90, angle: 120, origin: { x: 1, y: 0.75 }, startVelocity: 70, scalar: 1.1 });
  // 3) Shower of stars from centre
  setTimeout(() => {
    confetti({ ...base, particleCount: 160, spread: 170, origin: { x: 0.5, y: 0.4 }, startVelocity: 45, scalar: 0.9, shapes: ["star"], gravity: 0.9 });
  }, 130);

  // 4) A few staggered secondary bursts to keep the energy high
  [500, 1000, 1600].forEach((delay, i) => {
    setTimeout(() => {
      confetti({ ...base, particleCount: 130, spread: 120, origin: { x: i % 2 ? 0.28 : 0.72, y: 0.55 }, startVelocity: 55, scalar: 1.05 });
    }, delay);
  });

  // 5) Sustained shimmer falling from the top — runs until the 6s mark
  const duration = 6000;
  const end = Date.now() + duration;
  const rain = () => {
    const remaining = end - Date.now();
    if (remaining <= 0) return;
    confetti({ ...base, particleCount: 12, spread: 190, startVelocity: 24, gravity: 0.85, scalar: 0.9, origin: { x: Math.random(), y: -0.05 } });
    confetti({ ...base, particleCount: 6, spread: 200, startVelocity: 18, gravity: 0.8, scalar: 0.8, shapes: ["star"], origin: { x: Math.random(), y: -0.05 } });
    requestAnimationFrame(() => setTimeout(rain, 220));
  };
  rain();
};
